/**
 * Your Investors Screen
 *
 * The investors who have engaged with this founder's company — not a directory.
 * investor_profiles has no public read (E-11), so there is no "browse every
 * investor" set to show; the list comes from get_related_investors(), which
 * returns exactly those with a real relationship and says which one.
 *
 * The screen previously rendered MOCK_INVESTORS in demo and `[]` in
 * production, so every real founder saw an empty list under the heading
 * "Discover Investors" and was told their filters were too narrow.
 */

import React, { useState, useMemo } from 'react';
import {
  View,
  Text,
  ScrollView,
  TextInput,
  Pressable,
} from 'react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Stack, useRouter } from 'expo-router';
import Animated, { FadeIn, FadeInDown, FadeInUp } from 'react-native-reanimated';
import { noOutlineStyle } from '@/lib/platform';
import { usePhoneShell } from '@/lib/useShell';
import {
  ChevronLeft,
  Search,
  Star,
  MapPin,
  CheckCircle,
  MessageCircle,
  ChevronRight,
  SlidersHorizontal,
  X,
  Building2,
  DollarSign,
  Target,
  Users,
} from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { track, useTrackScreen } from '@/lib/analytics';
import { isWeb } from '@/lib/platform';
import { mode } from '@/lib/config/app-mode';
import { useResponsive } from '@/lib/useResponsive';
import type { InvestorProfile } from '@/lib/state/inverio-store';
import { useProfileStore } from '@/lib/state/profile-store';
import { useCreateConversation, isRelationshipDenied } from '@/lib/supabase';
import { useAlert } from '@/components/AlertDialog';
import { handleError } from '@/lib/errors';
import { formatCurrency } from '@/lib/utils/format';
import { STAGE_LABELS, INDUSTRIES } from '@/lib/state/marketplace';
import { AppLayout, Breadcrumb } from '@/components/AppLayout';
import { asAuthUserId, asInvestorProfileId } from '@/lib/ids';
import {
  useRelatedInvestors,
  RELATIONSHIP_LABELS,
  type RelatedInvestor,
} from '@/lib/investor/related-investors';

// Breadcrumbs for navigation (startups discovering investors)
const BREADCRUMBS: Breadcrumb[] = [
  { label: 'Messages', href: '/(startup)/startup-messages' },
  { label: 'Your Investors' },
];

// ============================================
// Mock Investors Data
// ============================================

// Extended type for display purposes (includes name for UI rendering)
interface InvestorWithName extends InvestorProfile {
  name: string;
}

/**
 * Sample data → the shape the real RPC returns, so demo builds and production
 * render through one code path. Demo investors are labelled `conversation`,
 * the weakest relationship, because nothing about sample data earns a stronger
 * one.
 */
function mockToRelated(inv: InvestorWithName): RelatedInvestor {
  return {
    // Sample data has no real id space; assert both so the demo path type-checks
    // through exactly the same code as production.
    profileId: asInvestorProfileId(inv.id),
    userId: asAuthUserId(inv.id),
    name: inv.name,
    firm: inv.firm ?? null,
    headline: null,
    location: inv.location ?? null,
    investmentFocus: inv.investmentFocus,
    stagePreference: inv.stagePreference,
    ticketSizeMin: inv.ticketSize.min,
    ticketSizeMax: inv.ticketSize.max,
    linkedInVerified: inv.linkedInVerified ?? false,
    relationship: 'conversation',
  };
}

const MOCK_INVESTORS: InvestorWithName[] = [
  {
    id: 'inv-1',
    name: 'Sarah Chen',
    firm: 'Sequoia Capital',
    investmentFocus: ['FinTech', 'AI/ML', 'SaaS'],
    stagePreference: ['seed', 'series-a'],
    ticketSize: { min: 500000, max: 5000000 },
    location: 'San Francisco, USA',
    portfolioCount: 28,
    linkedInVerified: true,
    seniorityLevel: 'veteran',
    yearsInvesting: 12,
  },
  {
    id: 'inv-2',
    name: 'Michael Roberts',
    firm: 'Andreessen Horowitz',
    investmentFocus: ['Consumer', 'Marketplace', 'E-Commerce'],
    stagePreference: ['pre-seed', 'seed'],
    ticketSize: { min: 100000, max: 1000000 },
    location: 'New York, USA',
    portfolioCount: 45,
    linkedInVerified: true,
    seniorityLevel: 'experienced',
    yearsInvesting: 8,
  },
  {
    id: 'inv-3',
    name: 'Emily Wong',
    firm: 'Index Ventures',
    investmentFocus: ['HealthTech', 'DeepTech', 'CleanTech'],
    stagePreference: ['seed', 'series-a'],
    ticketSize: { min: 1000000, max: 10000000 },
    location: 'London, UK',
    portfolioCount: 32,
    linkedInVerified: true,
    seniorityLevel: 'veteran',
    yearsInvesting: 15,
  },
  {
    id: 'inv-4',
    name: 'Thomas Mueller',
    firm: 'Lakestar',
    investmentFocus: ['Enterprise', 'SaaS', 'Cybersecurity'],
    stagePreference: ['series-a', 'series-b'],
    ticketSize: { min: 2000000, max: 15000000 },
    location: 'Zurich, Switzerland',
    portfolioCount: 18,
    linkedInVerified: true,
    seniorityLevel: 'experienced',
    yearsInvesting: 6,
  },
  {
    id: 'inv-5',
    name: 'Marie Dubois',
    firm: 'Balderton Capital',
    investmentFocus: ['FinTech', 'EdTech', 'Consumer'],
    stagePreference: ['pre-seed', 'seed'],
    ticketSize: { min: 250000, max: 2000000 },
    location: 'Paris, France',
    portfolioCount: 22,
    linkedInVerified: true,
    seniorityLevel: 'emerging',
    yearsInvesting: 3,
  },
  {
    id: 'inv-6',
    name: 'David Kim',
    firm: 'Accel Partners',
    investmentFocus: ['AI/ML', 'Enterprise', 'SaaS'],
    stagePreference: ['seed', 'series-a'],
    ticketSize: { min: 500000, max: 8000000 },
    location: 'Singapore',
    portfolioCount: 35,
    linkedInVerified: true,
    seniorityLevel: 'veteran',
    yearsInvesting: 10,
  },
];

// ============================================
// Investor Card
// ============================================

interface InvestorCardProps {
  investor: RelatedInvestor;
  matchScore?: number;
  onMessage: () => void;
  onOpen: () => void;
}

function InvestorCard({ investor, matchScore, onMessage, onOpen }: InvestorCardProps) {
  const colors = useThemeColors();
  // The card is now a real link: /investor-detail exists, so tapping it opens
  // the full profile instead of doing nothing.
  return (
    <AnimatedPressable
      onPress={onOpen}
      accessibilityLabel={`View ${investor.name}'s profile`}
      className="rounded-2xl overflow-hidden mb-3"
      style={{
        backgroundColor: colors.surface.primary,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.06,
        shadowRadius: 8,
        elevation: 3,
      }}
    >
      <View className="p-4">
        <View className="flex-row items-start gap-3">
          {/* Avatar */}
          <View
            className="w-14 h-14 rounded-full items-center justify-center"
            style={{ backgroundColor: colors.primary[100] }}
          >
            <Text style={{ color: colors.primary[700] }} className="text-lg font-bold">
              {investor.name.split(' ').map(n => n[0]).join('')}
            </Text>
          </View>

          <View className="flex-1">
            <View className="flex-row items-center gap-2">
              <Text style={{ color: colors.text.primary }} className="font-semibold text-base">
                {investor.name}
              </Text>
              {investor.linkedInVerified && (
                <CheckCircle size={16} color={colors.info} fill={colors.info} />
              )}
            </View>
            {investor.firm && (
              <Text style={{ color: colors.text.secondary }} className="text-sm">
                {investor.firm}
              </Text>
            )}

            <View className="flex-row items-center flex-wrap gap-2 mt-2">
              {/* Why this investor is here at all. Replaces a seniority badge
                  derived from a field the RPC does not return, and it is the
                  more useful fact: "on your cap table" and "asked for your
                  data room" are not the same relationship. */}
              <View
                className="px-2 py-1 rounded-lg"
                style={{ backgroundColor: colors.primary[50] }}
              >
                <Text style={{ color: colors.primary[700] }} className="text-xs font-medium" numberOfLines={1}>
                  {RELATIONSHIP_LABELS[investor.relationship] ?? 'Engaged with you'}
                </Text>
              </View>
              {investor.location ? (
                <View className="flex-row items-center gap-1">
                  <MapPin size={12} color={colors.text.tertiary} />
                  <Text style={{ color: colors.text.tertiary }} className="text-xs" numberOfLines={1}>
                    {investor.location.split(',')[0]}
                  </Text>
                </View>
              ) : null}
            </View>
          </View>

          {matchScore && (
            <View className="items-end">
              <View
                className="flex-row items-center gap-1 px-2 py-1 rounded-full"
                style={{ backgroundColor: colors.traffic.greenLight }}
              >
                <Star size={12} color={colors.success} fill={colors.success} />
                <Text style={{ color: colors.success }} className="text-xs font-bold">
                  {matchScore}%
                </Text>
              </View>
              <Text style={{ color: colors.text.tertiary }} className="text-xs mt-1">match</Text>
            </View>
          )}
        </View>

        {/* Investment Focus */}
        <View className="mt-3">
          <Text style={{ color: colors.text.tertiary }} className="text-xs mb-1.5">Investment Focus</Text>
          <View className="flex-row flex-wrap gap-1.5">
            {investor.investmentFocus.slice(0, 4).map((focus) => (
              <View
                key={focus}
                className="px-2 py-1 rounded-lg"
                style={{ backgroundColor: colors.surface.secondary }}
              >
                <Text style={{ color: colors.text.secondary }} className="text-xs">{focus}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Stats Row */}
        <View
          className="flex-row items-center justify-between mt-4 pt-3 border-t"
          style={{ borderColor: colors.border.light }}
        >
          <View className="flex-row items-center gap-4 flex-1">
            {(investor.ticketSizeMin > 0 || investor.ticketSizeMax > 0) && (
              <View className="flex-row items-center gap-1">
                <DollarSign size={14} color={colors.text.tertiary} />
                <Text style={{ color: colors.text.secondary }} className="text-xs" numberOfLines={1}>
                  {formatCurrency(investor.ticketSizeMin)} - {formatCurrency(investor.ticketSizeMax)}
                </Text>
              </View>
            )}
            {/* The "N investments" count is gone: it read portfolio_count, a
                stale aggregate that could claim 12 investments and list none.
                The real records live on the detail screen. */}
          </View>

          <AnimatedPressable
            onPress={(e) => {
              e.stopPropagation();
              onMessage();
            }}
            className="flex-row items-center gap-2 px-3 py-2 rounded-lg"
            style={{ backgroundColor: colors.primary[50] }}
          >
            <MessageCircle size={16} color={colors.primary[600]} />
            <Text style={{ color: colors.primary[600] }} className="text-sm font-medium">Connect</Text>
          </AnimatedPressable>
        </View>

        {/* Stage Preferences */}
        {investor.stagePreference.length > 0 && (
          <View className="flex-row items-center flex-wrap gap-2 mt-3">
            <Text style={{ color: colors.text.tertiary }} className="text-xs">Stages:</Text>
            {investor.stagePreference.map((stage, index) => (
              <Text
                key={`stage-${index}`}
                style={{ color: colors.text.secondary }}
                className="text-xs"
                numberOfLines={1}
              >
                {STAGE_LABELS[stage] ?? stage}
              </Text>
            ))}
          </View>
        )}
      </View>
    </AnimatedPressable>
  );
}

// ============================================
// Main Screen
// ============================================

export default function DiscoverInvestorsScreen() {
  useTrackScreen('DiscoverInvestors');
  const router = useRouter();
  const colors = useThemeColors();
  const phoneShell = usePhoneShell();
  const { isDesktop } = useResponsive();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [selectedStages, setSelectedStages] = useState<string[]>([]);

  const startupProfile = useProfileStore((s) => s.startupProfile);
  const createConversation = useCreateConversation();
  const { error: showError } = useAlert();

  // The real list: investors who have engaged with a company this founder is on
  // the team of. This screen previously read
  // `mode.showSampleData ? MOCK_INVESTORS : []`, so every production founder
  // saw an empty list and was told their FILTERS were too narrow.
  //
  // It is not a directory and never was — investor_profiles has no public read
  // (E-11). Sample data is still mapped in for demo builds so the walkthrough
  // keeps working.
  const { data: relatedInvestors = [], isLoading } = useRelatedInvestors();

  const sourceInvestors = useMemo<RelatedInvestor[]>(
    () => (mode.showSampleData ? MOCK_INVESTORS.map(mockToRelated) : relatedInvestors),
    [relatedInvestors]
  );

  // Filter investors
  const filteredInvestors = useMemo(() => {
    let result = sourceInvestors;

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(inv =>
        inv.name.toLowerCase().includes(query) ||
        inv.firm?.toLowerCase().includes(query) ||
        inv.investmentFocus.some(f => f.toLowerCase().includes(query)) ||
        (inv.location ?? '').toLowerCase().includes(query)
      );
    }

    // Industry filter
    if (selectedIndustries.length > 0) {
      result = result.filter(inv =>
        inv.investmentFocus.some(f => selectedIndustries.includes(f))
      );
    }

    // Stage filter
    if (selectedStages.length > 0) {
      result = result.filter(inv =>
        inv.stagePreference.some(s => selectedStages.includes(s))
      );
    }

    return result;
  }, [sourceInvestors, searchQuery, selectedIndustries, selectedStages]);

  // Calculate match scores
  const investorsWithMatch = useMemo(() => {
    if (!startupProfile) return filteredInvestors.map(inv => ({ investor: inv, matchScore: null }));

    return filteredInvestors.map(inv => {
      let score = 50; // Base score

      // Industry match
      if (inv.investmentFocus.some(f =>
        f.toLowerCase().includes(startupProfile.industry.toLowerCase()) ||
        startupProfile.industry.toLowerCase().includes(f.toLowerCase())
      )) {
        score += 25;
      }

      // Stage match
      if (inv.stagePreference.includes(startupProfile.stage)) {
        score += 15;
      }

      // Ticket size match
      if (startupProfile.fundingTarget >= inv.ticketSizeMin &&
          startupProfile.fundingTarget <= inv.ticketSizeMax) {
        score += 10;
      }

      return { investor: inv, matchScore: Math.min(100, score) };
    }).sort((a, b) => (b.matchScore || 0) - (a.matchScore || 0));
  }, [filteredInvestors, startupProfile]);

  // Handle connect/message. Conversation creation is RLS relationship-gated:
  // a founder can only open a thread with an investor who has already engaged
  // (in dealflow OR granted data-room access). Cold outreach is denied, so we
  // surface an honest message rather than silently failing or faking a thread.
  const handleConnect = async (investor: RelatedInvestor) => {
    if (!startupProfile?.id) {
      showError('Complete Your Profile', 'Set up your startup profile before reaching out to investors.');
      return;
    }
    try {
      await createConversation.mutateAsync({
        startupId: startupProfile.id,
        // investor_profiles.id — the space conversations.investor_profile_id
        // keys on. (That column was renamed in 20260801090000; this comment
        // still named the old one five days later. ARCH-11 exists for exactly
        // that drift and could not see it — the file was blanket-allowlisted.)
        investorId: investor.profileId,
      });
      router.push('/(startup)/startup-messages');
    } catch (err) {
      if (isRelationshipDenied(err)) {
        // Cold outreach is no longer categorically denied — it is gated on the
        // founder's plan AND the investor's own setting. Name whichever
        // actually blocked, so an upgrade is never sold against an investor who
        // has opted out.
        // This screen lists only investors who have ALREADY engaged, so a
        // denial here means the engagement has not reached the threshold that
        // opens a channel — not that outreach is forbidden outright.
        showError(
          'Not Available Yet',
          "You can message an investor once they've marked your startup as interested, or granted you data-room access.",
        );
        return;
      }
      const { message, isCancelled } = handleError(err, { context: 'DiscoverInvestors.handleConnect' });
      if (!isCancelled) showError('Could Not Start Conversation', message);
    }
  };

  const toggleIndustry = (industry: string) => {
    setSelectedIndustries(prev =>
      prev.includes(industry) ? prev.filter(i => i !== industry) : [...prev, industry]
    );
  };

  const toggleStage = (stage: string) => {
    setSelectedStages(prev =>
      prev.includes(stage) ? prev.filter(s => s !== stage) : [...prev, stage]
    );
  };

  const content = (
    <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
      <SafeAreaView edges={isWeb ? [] : ['top']} className="flex-1">
        {/* Header - Mobile only */}
        {phoneShell && (
          <Animated.View
            entering={FadeInDown.duration(400)}
            className="px-5 py-4"
            style={{ backgroundColor: colors.surface.primary }}
          >
            <View className="flex-row items-center gap-3 mb-4">
              <Pressable
                onPress={() => router.back()}
                className="w-10 h-10 rounded-full items-center justify-center"
                style={{ backgroundColor: colors.surface.secondary }}
              >
                <ChevronLeft size={24} color={colors.text.primary} />
              </Pressable>
              <View className="flex-1">
                <Text style={{ color: colors.text.primary }} className="text-xl font-bold">
                  Find Investors
                </Text>
                <Text style={{ color: colors.text.secondary }} className="text-sm">
                  {filteredInvestors.length} investor{filteredInvestors.length === 1 ? '' : 's'} engaged with you
                </Text>
              </View>
            </View>

            {/* Search Bar */}
            <View
              className="flex-row items-center gap-2 px-4 rounded-xl"
              style={{
                backgroundColor: colors.surface.secondary,
                height: 48,
              }}
            >
              <Search size={20} color={colors.text.tertiary} />
              <TextInput
                value={searchQuery}
                onChangeText={setSearchQuery}
                placeholder="Search investors, firms, focus areas..."
                placeholderTextColor={colors.text.tertiary}
                className="flex-1 text-base"
                style={noOutlineStyle({ color: colors.text.primary })}
              />
              {searchQuery.length > 0 && (
                <Pressable onPress={() => setSearchQuery('')} className="p-1">
                  <X size={18} color={colors.text.tertiary} />
                </Pressable>
              )}
            </View>
          </Animated.View>
        )}

        {/* Web Search Bar */}
        {isWeb && (
          <View className="px-5 py-4">
            <View
              className="flex-row items-center gap-2 px-4 rounded-xl"
              style={{
                backgroundColor: colors.surface.primary,
                borderWidth: 1,
                borderColor: colors.border.light,
                height: 48,
              }}
            >
              <Search size={20} color={colors.text.tertiary} />
              <TextInput
                value={searchQuery}
                onChangeText={setSearchQuery}
                placeholder="Search investors, firms, focus areas..."
                placeholderTextColor={colors.text.tertiary}
                className="flex-1 text-base"
                style={noOutlineStyle({ color: colors.text.primary })}
              />
              {searchQuery.length > 0 && (
                <Pressable onPress={() => setSearchQuery('')} className="p-1">
                  <X size={18} color={colors.text.tertiary} />
                </Pressable>
              )}
            </View>
            <Text style={{ color: colors.text.secondary }} className="text-sm mt-2">
              {filteredInvestors.length} investor{filteredInvestors.length === 1 ? '' : 's'} engaged with you
            </Text>
          </View>
        )}

        <ScrollView className="flex-1" showsVerticalScrollIndicator={false}>
          {/* Quick Filters */}
          <Animated.View
            entering={FadeInUp.delay(100).duration(400)}
            className="px-5 py-3"
          >
            {/* Industry Filters */}
            <Text style={{ color: colors.text.secondary }} className="text-xs font-medium mb-2">
              Investment Focus
            </Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ flexGrow: 0 }}>
              <View className="flex-row gap-2">
                {['FinTech', 'AI/ML', 'SaaS', 'HealthTech', 'Consumer', 'Enterprise'].map(industry => (
                  <AnimatedPressable
                    key={industry}
                    onPress={() => toggleIndustry(industry)}
                    className="px-3 py-1.5 rounded-full"
                    style={{
                      backgroundColor: selectedIndustries.includes(industry)
                        ? colors.primary[600]
                        : colors.surface.primary,
                      borderWidth: 1,
                      borderColor: selectedIndustries.includes(industry)
                        ? colors.primary[600]
                        : colors.border.light,
                    }}
                  >
                    <Text
                      style={{
                        color: selectedIndustries.includes(industry)
                          ? 'white'
                          : colors.text.secondary,
                      }}
                      className="text-sm"
                    >
                      {industry}
                    </Text>
                  </AnimatedPressable>
                ))}
              </View>
            </ScrollView>

            {/* Stage Filters */}
            <Text style={{ color: colors.text.secondary }} className="text-xs font-medium mb-2 mt-3">
              Stage Preference
            </Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ flexGrow: 0 }}>
              <View className="flex-row gap-2">
                {['pre-seed', 'seed', 'series-a', 'series-b'].map(stage => (
                  <AnimatedPressable
                    key={stage}
                    onPress={() => toggleStage(stage)}
                    className="px-3 py-1.5 rounded-full"
                    style={{
                      backgroundColor: selectedStages.includes(stage)
                        ? colors.traffic.green
                        : colors.surface.primary,
                      borderWidth: 1,
                      borderColor: selectedStages.includes(stage)
                        ? colors.traffic.green
                        : colors.border.light,
                    }}
                  >
                    <Text
                      style={{
                        color: selectedStages.includes(stage)
                          ? 'white'
                          : colors.text.secondary,
                      }}
                      className="text-sm"
                    >
                      {STAGE_LABELS[stage]}
                    </Text>
                  </AnimatedPressable>
                ))}
              </View>
            </ScrollView>
          </Animated.View>

          {/* Results */}
          <Animated.View
            entering={FadeInUp.delay(200).duration(400)}
            className="px-5 pb-8"
          >
            <View className="flex-row items-center justify-between mb-4">
              <Text style={{ color: colors.text.primary }} className="font-semibold">
                {/* Not "Recommended For You" — nothing here is a
                    recommendation. These are people who already reached out. */}
                {searchQuery || selectedIndustries.length || selectedStages.length
                  ? 'Matching Investors'
                  : 'Engaged With You'}
              </Text>
            </View>

            {investorsWithMatch.map(({ investor, matchScore }, index) => (
              <Animated.View
                key={`investor-${investor.profileId}`}
                entering={FadeIn.delay(100 + index * 50).duration(200)}
              >
                <InvestorCard
                  investor={investor}
                  matchScore={matchScore || undefined}
                  onMessage={() => handleConnect(investor)}
                  onOpen={() =>
                    router.push(
                      `/investor-detail?id=${investor.profileId}&relationship=${investor.relationship}`
                    )
                  }
                />
              </Animated.View>
            ))}

            {/* Two different empty states.
                The old copy said "No investors match your criteria — try
                adjusting your filters" in BOTH cases, which blamed the
                founder's filters for a dataset that was empty regardless. */}
            {investorsWithMatch.length === 0 && !isLoading && (
              <View className="items-center py-12 px-6">
                <Users size={48} color={colors.text.tertiary} />
                {sourceInvestors.length === 0 ? (
                  <>
                    <Text
                      style={{ color: colors.text.primary }}
                      className="text-center mt-4 font-semibold"
                    >
                      No investors yet
                    </Text>
                    <Text
                      style={{ color: colors.text.secondary }}
                      className="text-center mt-2 text-sm leading-5"
                    >
                      Investors appear here once they engage with you — when one starts
                      a conversation, requests your data room, or begins due diligence.
                    </Text>
                  </>
                ) : (
                  <Text style={{ color: colors.text.secondary }} className="text-center mt-4">
                    None of your investors match these filters.{'\n'}Try widening them.
                  </Text>
                )}
              </View>
            )}
          </Animated.View>
        </ScrollView>
      </SafeAreaView>
    </View>
  );

  if (isWeb) {
    return (
      <AppLayout title="Your Investors" breadcrumbs={BREADCRUMBS}>
        {content}
      </AppLayout>
    );
  }

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      {content}
    </>
  );
}
